import { personalTrainerPackages } from 'data/PersonalTrainerPackages'
import { localBusinessNode, LOCAL_BUSINESS_ID } from 'utils/local-business-schema'

/**
 * JSON-LD graph for /sg/personal-trainer.
 *
 * Previously a ~5KB single-line template literal inside the page, which made it
 * effectively uneditable and hid two defects:
 *
 *  1. `aggregateRating` appeared on BOTH a LocalBusiness and a Product node.
 *  2. The page sells a service, not a Product. The Product node is gone; a
 *     Service node carries the real price range as an AggregateOffer, and the
 *     WebPage references it by @id instead of inlining a second copy.
 *
 * The business itself now comes from utils/local-business-schema so it is
 * byte-identical on every page. It deliberately carries no aggregateRating:
 * Google makes a business ineligible for star ratings on reviews it controls
 * about itself, and forbids aggregating ratings from other sites - the Google
 * rating is shown as visible page content instead.
 *
 * The price range is derived from the package data.
 */

const SITE = "https://www.stringssg.com"
const PAGE = `${SITE}/sg/personal-trainer`

const PAGE_DESCRIPTION =
    "Book a certified personal trainer in Singapore from $84/session. Train at home, your condo gym or outdoors. Free consultation, transparent pricing, 5,000+ clients since 2018."

const HERO_IMAGE =
    "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/female-personal-trainer-training-girl-at-gym.jpeg"

const LOGO = "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"

// Derived so the advertised range cannot drift from the packages on the page.
const allPrices = Object.values(personalTrainerPackages)
    .flatMap((tier) => tier.packages.map((pkg) => pkg.perSessionPrice))
const lowPrice = Math.min(...allPrices)
const highPrice = Math.max(...allPrices)

export const buildPersonalTrainerSchema = ({ pageTitle, modifiedDate }) => ({
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "BreadcrumbList",
            "@id": `${PAGE}/#breadcrumblist`,
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    // Singapore's home, as in utils/breadcrumb ("/" is the global page).
                    item: { "@type": "WebPage", "@id": `${SITE}/sg`, name: "Singapore", url: `${SITE}/sg` }
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    item: {
                        "@type": "WebPage",
                        "@id": PAGE,
                        name: "Personal Trainer Singapore",
                        description: PAGE_DESCRIPTION,
                        url: PAGE
                    }
                }
            ]
        },
        localBusinessNode,
        {
            // Replaces the former Product node. No aggregateRating here.
            "@type": "Service",
            "@id": `${PAGE}/#service`,
            name: "Personal Trainer Singapore",
            url: PAGE,
            description:
                "Certified personal trainers in Singapore for weight loss, strength training and home-based fitness sessions, at your home, condo gym or a nearby park.",
            image: HERO_IMAGE,
            serviceType: "Personal Training",
            areaServed: { "@type": "Country", name: "Singapore" },
            provider: { "@id": LOCAL_BUSINESS_ID },
            offers: {
                "@type": "AggregateOffer",
                priceCurrency: "SGD",
                lowPrice: String(lowPrice),
                highPrice: String(highPrice),
                availability: "https://schema.org/InStock",
                offerCount: String(allPrices.length)
            }
        },
        // No VideoObject: the clip it described is not embedded on the page,
        // and its view count was not a real figure. Google requires video
        // markup to describe a video that is actually on the page.
        {
            "@type": "WebPage",
            "@id": `${PAGE}/#webpage`,
            url: PAGE,
            name: pageTitle,
            description: PAGE_DESCRIPTION,
            inLanguage: "en-SG",
            isPartOf: { "@id": `${SITE}/#website` },
            breadcrumb: { "@id": `${PAGE}/#breadcrumblist` },
            // First published at this URL (moved under /sg on 2022-12-07). Must
            // match article:published_time in pages/sg/personal-trainer/index.js.
            datePublished: "2022-12-07T09:00:00+08:00",
            dateModified: modifiedDate,
            // References the Service node rather than inlining a second copy of it.
            mainEntity: { "@id": `${PAGE}/#service` }
        },
        {
            "@type": "Organization",
            "@id": `${SITE}/#organization`,
            name: "StringsSG",
            url: `${SITE}/`,
            logo: LOGO,
            foundingDate: "2018",
            description:
                "Singapore personal training service since 2018, connecting clients with certified personal trainers."
        },
        {
            "@type": "WebSite",
            "@id": `${SITE}/#website`,
            url: `${SITE}/`,
            name: "StringsSG",
            inLanguage: "en-SG",
            publisher: { "@id": `${SITE}/#organization` }
            // No SearchAction: there is no /search route, so a sitelinks
            // searchbox target would point at a 404.
        }
    ]
})
